import React, { useMemo } from 'react';
import DefaultContainer from '../../components/DefaultContainer';
import { Button, Text } from '~/components';
import { Components } from '~/scenes/HiredInsurance/components';
import { useRoute, type RouteProp } from '@react-navigation/native';
import type { RootStackParamList } from '~/navigation/types';
import { useUserInfoStore } from '~/stores/userInfo';
import useAppNavigation from '~/hooks/useAppNavigation';
import GeneralInfoFooter from '~/scenes/IncomeProtection/components/GeneralInfoFooter';
import { isWeb } from '~/utils/device';
import addMonths from 'date-fns/addMonths';
import parseISO from 'date-fns/parseISO';
import format from 'date-fns/format';
import { formatMoney } from '~/utils';
import { View } from 'react-native';

const HiredInsuranceDetailsIncomeProtection: React.FC = () => {
  const { navigate, goBack } = useAppNavigation();

  const contractedServices = useUserInfoStore(
    (state) => state.user?.contractedServices
  );

  const {
    params: { id: certificateCode },
  } =
    useRoute<
      RouteProp<
        RootStackParamList,
        'MarisaServicos_HiredInsuranceDetailsIncomeProtection'
      >
    >();

  const contractedService = contractedServices?.find(
    (service) => service.certificateCode === certificateCode
  );

  const isCanceled = useMemo(
    () => Boolean(contractedService?.cancellationDate),
    [contractedService?.cancellationDate]
  );

  if (!contractedService) return null;

  return (
    <DefaultContainer type="income-protection" className="p-6">
      <Text className="text-lg text-gray-700 mb-6">
        Detalhes da contratação
      </Text>

      <Components.InsuranceStatus data={contractedService} />

      {!!contractedService.holderName && (
        <>
          <Components.Divider />

          <Components.Container>
            <Components.LeftText>Nome do Titular</Components.LeftText>
            <Components.RightText>
              {contractedService.holderName}
            </Components.RightText>
          </Components.Container>
        </>
      )}

      {!!contractedService.value && (
        <>
          <Components.Divider />

          <Components.Container>
            <Components.LeftText>Valor mensal</Components.LeftText>
            <Text className="text-gray-700 text-sm">
              R${' '}
              <Text className="font-medium text-xl">
                {formatMoney(contractedService.value)}
              </Text>
              /mês
            </Text>
          </Components.Container>
        </>
      )}

      {!!contractedService.saleDate &&
        !!contractedService.validity &&
        !isCanceled && (
          <>
            <Components.Divider />

            <Components.Container
              additionalContent={
                <View className="mt-4 px-2 py-1 bg-secondary-200">
                  <Text className="text-xs text-gray-400">
                    Carência de 30 dias à partir da data da contratação.
                  </Text>
                </View>
              }
            >
              <Components.LeftText>Vigente até</Components.LeftText>
              <Components.DateText>
                {format(
                  addMonths(
                    parseISO(contractedService.saleDate),
                    contractedService.validity
                  ),
                  'dd/MM/yyyy'
                )}
              </Components.DateText>
            </Components.Container>
          </>
        )}

      {!isCanceled && (
        <>
          <Components.Divider />

          <Components.Container>
            <Components.LeftText>Coberturas do seguro</Components.LeftText>
            <Components.ChevronButton
              onPress={() =>
                navigate(
                  'MarisaServicos_HiredInsuranceCoverageIncomeProtection',
                  {
                    id: certificateCode,
                  }
                )
              }
            />
          </Components.Container>
        </>
      )}

      <Components.Divider />

      <Components.Container>
        <Components.LeftText>
          Central de Atendimento ao Segurado
        </Components.LeftText>
        <Components.ChevronButton
          onPress={() =>
            navigate(
              'MarisaServicos_HiredInsuranceCallCenterIncomeProtection',
              {
                id: certificateCode,
              }
            )
          }
        />
      </Components.Container>

      <Components.Divider />

      {!isCanceled && (
        <>
          <Components.InsurancePolicyInfo
            policyNumber={contractedService.policyNumber}
            policyUrl={contractedService.policyUrl}
          />

          <Components.Divider />

          <Components.Container>
            <Components.LeftText>Cancelar seguro</Components.LeftText>
            <Components.ChevronButton
              onPress={() =>
                navigate(
                  'MarisaServicos_HiredInsuranceCancelIncomeProtection',
                  {
                    id: certificateCode,
                  }
                )
              }
            />
          </Components.Container>

          {contractedService.luckyNumber && (
            <>
              <Components.Divider />

              <Components.Container>
                <Components.LeftText>Número da sorte</Components.LeftText>
                <Components.RightText>
                  {contractedService.luckyNumber}
                </Components.RightText>
              </Components.Container>
            </>
          )}

          <Components.Divider />
        </>
      )}

      <GeneralInfoFooter />

      <Components.Divider />

      {isCanceled && (
        <Button
          onPress={() =>
            navigate('MarisaServicos_IncomeProtectionContract', {})
          }
          className="mt-3"
        >
          Contratar seguro novamente
        </Button>
      )}

      <Button
        variant="tertiary"
        className={isWeb ? 'mt-14' : 'mt-4'}
        onPress={goBack}
      >
        VOLTAR
      </Button>
    </DefaultContainer>
  );
};

export default HiredInsuranceDetailsIncomeProtection;
